import { useState, useRef, useEffect } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { getUnidades, createUnidad, updateUnidad, darDeBajaUnidad, deleteUnidad, getProyectosUnidades } from '../api/client'
import Modal from '../components/Modal'
import PageHeader from '../components/PageHeader'
import TableFilters from '../components/TableFilters'
import { EncabezadoFijo } from '../components/unidades/PlanillaColumnas'
import { ordenar, siguienteOrden } from '../components/unidades/ordenLogica'
import Planilla from '../components/unidades/Planilla'
import HistorialLista from '../components/unidades/HistorialLista'
import UnidadPanel from '../components/unidades/UnidadPanel'
import { formatFecha } from '../components/unidades/tipos'
import { usePuedeEditarMantenimiento } from '../utils/mantenimiento'
import { ControlZoom, ZonaZoom } from '../components/ot/zoom'
import { useZoom } from '../components/ot/useZoom'

// The closed list, in the backend's spelling (models.UnidadEstado). The value
// is what's stored; the i18n key is only for what's shown.
const ESTADOS = [
  { value: 'Operativo',     key: 'operativo',     cls: 'bg-green-100 text-green-700' },
  { value: 'Mantenimiento', key: 'mantenimiento', cls: 'bg-yellow-100 text-yellow-800' },
  { value: 'Baja',          key: 'baja',          cls: 'bg-red-100 text-red-700' },
]

// The Tabla's own columns, for sorting it.
const COLUMNAS_TABLA = [...['codigo', 'proyecto', 'estado_actual'].map(id => ({ id, tipo: 'texto', fijo: true })),
  { id: 'fecha_ingreso', tipo: 'fecha', fijo: true }]

const EMPTY_FORM = { codigo: '', proyecto: '', estado_actual: 'Operativo', fecha_ingreso: '' }

export default function Unidades() {
  const { t } = useTranslation('engineering')
  // Everyone in Operaciones sees the units; only some may change them.
  const puedeEditar = usePuedeEditarMantenimiento()
  const [unidades, setUnidades] = useState([])
  const [loading, setLoading] = useState(true)
  // The form's Proyecto choices: the registered projects of the Mantenimiento
  // area (Administración > Proyectos). A select, not free text -- the backend
  // refuses (422 proyecto_desconocido) a name that isn't a registered project.
  const [proyectos, setProyectos] = useState([])
  const loadProyectos = () => getProyectosUnidades().then(setProyectos).catch(() => {})
  useEffect(() => { loadProyectos() }, [])
  const [loadError, setLoadError] = useState('')
  const [params, setParams] = useState({})
  // 'tabla' = the list with its form; 'planilla' = the per-project grid;
  // 'historial' = every recorded change.
  const [vista, setVista] = useState('tabla')
  const zoom = useZoom('mant_zoom_unidades')
  // The unit whose side panel (conversation + history) is open, if any.
  // Kept in the address (?unidad=<id>) so a parte's panel can link straight
  // to its unit, the same way a unit links to its partes.
  const [searchParams, setSearchParams] = useSearchParams()
  const panelId = searchParams.get('unidad') ? Number(searchParams.get('unidad')) : null
  // The Tabla's row order, by one of its columns (ordenLogica.js); null = by code, as loaded.
  const [orden, setOrden] = useState(null)
  const ordenarPor = (campo, dir) => setOrden(o => siguienteOrden(o, campo, dir))
  const setPanelId = (id) => setSearchParams(sp => {
    const next = new URLSearchParams(sp)
    if (id == null) next.delete('unidad')
    else next.set('unidad', String(id))
    return next
  })
  // null = closed, 'new' = adding, otherwise the unidad being edited
  const [editing, setEditing] = useState(null)
  const [form, setForm] = useState(EMPTY_FORM)
  const [saving, setSaving] = useState(false)
  const [formError, setFormError] = useState('')
  // Only the newest request may fill the table: a slow response for an older
  // filter must not overwrite the one the user is looking at now.
  const requestId = useRef(0)

  const estadoLabel = (v) => {
    const e = ESTADOS.find(x => x.value === v)
    return e ? t(`unidades.estados.${e.key}`) : v
  }

  const load = async (p = params) => {
    const id = ++requestId.current
    setLoading(true)
    try {
      const data = await getUnidades(p)
      if (id !== requestId.current) return
      setUnidades(data)
      setLoadError('')
    } catch {
      if (id === requestId.current) setLoadError(t('unidades.loadFailed'))
    } finally {
      if (id === requestId.current) setLoading(false)
    }
  }

  const onFiltersChange = (p) => { setParams(p); load(p) }

  const openAdd = () => { setForm(EMPTY_FORM); setFormError(''); setEditing('new') }
  const openEdit = (u) => {
    setForm({ codigo: u.codigo, proyecto: u.proyecto || '', estado_actual: u.estado_actual, fecha_ingreso: u.fecha_ingreso || '' })
    setFormError('')
    setEditing(u)
  }
  const close = () => setEditing(null)

  const handleSave = async (e) => {
    e.preventDefault()
    const payload = {
      codigo: form.codigo.trim(),
      proyecto: form.proyecto.trim() || null,
      fecha_ingreso: form.fecha_ingreso || null,
    }
    // Mantenimiento belongs to the unit's F/S parte: the form never sends it.
    if (form.estado_actual !== 'Mantenimiento') payload.estado_actual = form.estado_actual
    if (!payload.codigo) { setFormError(t('unidades.codigoRequired')); return }
    setSaving(true)
    try {
      if (editing === 'new') await createUnidad(payload)
      else await updateUnidad(editing.id, payload)
      close()
      await load()
    } catch (err) {
      const d = err.response?.data?.detail
      setFormError(d?.code === 'estado_por_parte' ? t('unidades.estadoPorParte')
        : d?.code === 'proyecto_desconocido' ? (d.message || t('unidades.proyectoDesconocido'))
        : err.response?.status === 409 ? t('unidades.codigoDuplicate', { codigo: payload.codigo })
        : t('unidades.saveFailed'))
    } finally {
      setSaving(false)
    }
  }

  const handleBaja = async (u) => {
    if (!confirm(t('unidades.bajaConfirm', { codigo: u.codigo }))) return
    try { await darDeBajaUnidad(u.id); await load() }
    catch (err) {
      alert(err.response?.data?.detail?.code === 'estado_por_parte' ? t('unidades.estadoPorParte') : t('unidades.saveFailed'))
    }
  }

  const handleDelete = async (u) => {
    if (!confirm(t('unidades.deleteConfirm', { codigo: u.codigo }))) return
    try { await deleteUnidad(u.id); await load() }
    catch (err) { alert(err.response?.status === 409 ? t('unidades.deleteHasPartes', { codigo: u.codigo }) : t('unidades.deleteFailed')) }
  }

  const inputCls = 'w-full border border-gray-200 rounded-lg px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-gray-200'
  const hasFilters = Object.keys(params).length > 0

  return (
    <div className="p-8">
      <div className="mb-6">
        <PageHeader
          title={t('unidades.title')}
          subtitle={t('unidades.subtitle')}
          actions={puedeEditar && (
            <button onClick={openAdd}
              className="bg-gray-900 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-gray-800 transition-colors">
              {t('unidades.add')}
            </button>
          )}
        />
      </div>

      <div className="flex flex-wrap items-center gap-3 mb-5">
      <div className="inline-flex rounded-lg border border-gray-200 bg-white p-0.5">
        {['tabla', 'planilla', 'historial'].map(v => (
          <button key={v} type="button" onClick={() => setVista(v)}
            className={`px-3.5 py-1.5 rounded-md text-sm font-medium transition-colors ${
              vista === v ? 'bg-gray-900 text-white' : 'text-gray-500 hover:text-gray-900'}`}>
            {t(`unidades.vistas.${v}`)}
          </button>
        ))}
      </div>
      <ControlZoom z={zoom} />
      </div>

      <ZonaZoom z={zoom} libre>
      {vista === 'historial' ? (
        <HistorialLista ambito="unidades" onOpenUnidad={setPanelId} />
      ) : vista === 'planilla' ? (
        <Planilla estados={ESTADOS} estadoLabel={estadoLabel} onOpenUnidad={setPanelId} />
      ) : (<>
      <div className="mb-5">
        <TableFilters
          search={{ param: 'buscar', placeholder: t('unidades.searchPlaceholder') }}
          filters={[
            { param: 'estado_actual', label: t('unidades.estado'), type: 'select',
              options: ESTADOS.map(e => ({ value: e.value, label: estadoLabel(e.value) })) },
            { param: 'codigo', label: t('unidades.codigo'), type: 'text' },
          ]}
          onChange={onFiltersChange}
        />
      </div>

      {loadError ? (
        <div className="text-red-500 text-sm">{loadError}</div>
      ) : loading && unidades.length === 0 ? (
        <div className="text-gray-400 text-sm">{t('unidades.loading')}</div>
      ) : unidades.length === 0 ? (
        <div className="text-center py-12 text-gray-400">
          <p>{hasFilters ? t('unidades.noMatches') : t('unidades.empty')}</p>
        </div>
      ) : (
        <div className={`bg-white rounded-xl border border-gray-200 overflow-hidden transition-opacity ${loading ? 'opacity-60' : ''}`}>
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-100 bg-gray-50">
                <th className="text-left px-5 py-3 font-semibold text-gray-600"><EncabezadoFijo label={t('unidades.codigo')} campo="codigo" orden={orden} onOrdenar={ordenarPor} /></th>
                <th className="text-left px-5 py-3 font-semibold text-gray-600"><EncabezadoFijo label={t('unidades.proyecto')} campo="proyecto" orden={orden} onOrdenar={ordenarPor} /></th>
                <th className="text-left px-5 py-3 font-semibold text-gray-600"><EncabezadoFijo label={t('unidades.estado')} campo="estado_actual" orden={orden} onOrdenar={ordenarPor} /></th>
                <th className="text-left px-5 py-3 font-semibold text-gray-600"><EncabezadoFijo label={t('unidades.fechaIngreso')} campo="fecha_ingreso" tipo="fecha" orden={orden} onOrdenar={ordenarPor} /></th>
                {puedeEditar && <th className="px-5 py-3"></th>}
              </tr>
            </thead>
            <tbody>
              {ordenar(unidades, orden, COLUMNAS_TABLA).map((u, i) => {
                const estado = ESTADOS.find(e => e.value === u.estado_actual)
                return (
                  <tr key={u.id} className={`border-b border-gray-50 hover:bg-gray-50 ${i === unidades.length - 1 ? 'border-b-0' : ''}`}>
                    <td className="px-5 py-3 font-medium text-gray-900">
                      <button type="button" onClick={() => setPanelId(u.id)} title={t('unidades.panel.open')}
                        className="hover:underline">{u.codigo}</button>
                    </td>
                    <td className="px-5 py-3 text-gray-600">{u.proyecto || '—'}</td>
                    <td className="px-5 py-3">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${estado?.cls || 'bg-gray-100 text-gray-600'}`}>
                        {estadoLabel(u.estado_actual)}
                      </span>
                    </td>
                    <td className="px-5 py-3 text-gray-600 whitespace-nowrap">{u.fecha_ingreso ? formatFecha(u.fecha_ingreso) : '—'}</td>
                    {puedeEditar && <td className="px-5 py-3 text-right whitespace-nowrap">
                      <button onClick={() => openEdit(u)} className="text-gray-400 hover:text-gray-900 text-xs font-medium mr-3">
                        {t('unidades.edit')}
                      </button>
                      {u.estado_actual === 'Operativo' && (
                        <button onClick={() => handleBaja(u)} className="text-gray-400 hover:text-amber-600 text-xs font-medium mr-3">
                          {t('unidades.darDeBaja')}
                        </button>
                      )}
                      <button onClick={() => handleDelete(u)} className="text-gray-400 hover:text-red-500 text-xs font-medium">
                        {t('unidades.delete')}
                      </button>
                    </td>}
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}
      </>)}
      </ZonaZoom>

      {panelId != null && (
        <UnidadPanel key={panelId} unidadId={panelId} estados={ESTADOS} estadoLabel={estadoLabel}
          onClose={() => setPanelId(null)} />
      )}

      {editing && (
        <Modal title={editing === 'new' ? t('unidades.addTitle') : t('unidades.editTitle')} onClose={close}>
          <form onSubmit={handleSave} className="space-y-4">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">{t('unidades.codigo')} *</label>
              <input type="text" autoFocus className={inputCls} value={form.codigo} placeholder="E325"
                onChange={e => setForm(f => ({ ...f, codigo: e.target.value }))} />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">{t('unidades.proyecto')}</label>
              <select className={inputCls} value={form.proyecto}
                onChange={e => setForm(f => ({ ...f, proyecto: e.target.value }))}>
                <option value="">{t('unidades.sinProyecto')}</option>
                {proyectos.map(p => <option key={p} value={p}>{p}</option>)}
                {/* A unit still carrying a name that is no longer a registered project. */}
                {form.proyecto && !proyectos.includes(form.proyecto) && (
                  <option value={form.proyecto}>{form.proyecto} {t('unidades.proyectoNoRegistrado')}</option>
                )}
              </select>
              <p className="text-xs text-gray-400 mt-1">{t('unidades.proyectoHint')}</p>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">{t('unidades.fechaIngreso')}</label>
              <input type="date" className={inputCls} value={form.fecha_ingreso}
                onChange={e => setForm(f => ({ ...f, fecha_ingreso: e.target.value }))} />
              <p className="text-xs text-gray-400 mt-1">{t('unidades.fechaIngresoHint')}</p>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">{t('unidades.estado')} *</label>
              {form.estado_actual === 'Mantenimiento' ? (
                <>
                  <select className={`${inputCls} bg-gray-50 text-gray-500`} value="Mantenimiento" disabled>
                    <option value="Mantenimiento">{estadoLabel('Mantenimiento')}</option>
                  </select>
                  <p className="text-[11px] text-amber-700 mt-1">{t('unidades.estadoBloqueado')}</p>
                </>
              ) : (
                <>
                  {/* Mantenimiento isn't offered: only an F/S parte sets it. */}
                  <select className={inputCls} value={form.estado_actual}
                    onChange={e => setForm(f => ({ ...f, estado_actual: e.target.value }))}>
                    {ESTADOS.filter(e => e.value !== 'Mantenimiento').map(e => (
                      <option key={e.value} value={e.value}>{estadoLabel(e.value)}</option>
                    ))}
                  </select>
                  <p className="text-[11px] text-gray-400 mt-1">{t('unidades.estadoHint')}</p>
                </>
              )}
            </div>
            {formError && <p className="text-red-500 text-xs">{formError}</p>}
            <div className="flex justify-end gap-2 pt-2">
              <button type="button" onClick={close}
                className="bg-white border border-gray-200 text-gray-600 px-4 py-2 rounded-lg text-sm font-medium hover:bg-gray-50 transition-colors">
                {t('unidades.cancel')}
              </button>
              <button type="submit" disabled={saving}
                className="bg-gray-900 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-gray-800 disabled:opacity-50 transition-colors">
                {saving ? t('unidades.saving') : t('unidades.save')}
              </button>
            </div>
          </form>
        </Modal>
      )}
    </div>
  )
}
